import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import DashOverview from '../components/dash/Overview';
import DashTransactions from '../components/dash/Transactions';
import DashUnits from '../components/dash/Units';
import DashGoals from '../components/dash/Goals';
import DashWithdrawals from '../components/dash/Withdrawals';
import DashSettings from '../components/dash/Settings';
import DashOverlay from '../components/dash/Overlay';
import { ChartBar, Receipt, Coffee, Target, Bank, GearSix, UserCircle, Broadcast } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { resolveFileUrl } from '../lib/api';

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const current = location.pathname.replace('/dashboard', '') || '/';

  const items = [
    { to: '/dashboard', label: 'Overview', icon: ChartBar, match: '/' },
    { to: '/dashboard/transactions', label: 'Transaksi', icon: Receipt, match: '/transactions' },
    { to: '/dashboard/units', label: 'Unit Traktiran', icon: Coffee, match: '/units' },
    { to: '/dashboard/goals', label: 'Goals', icon: Target, match: '/goals' },
    { to: '/dashboard/withdrawals', label: 'Penarikan', icon: Bank, match: '/withdrawals' },
    { to: '/dashboard/overlay', label: 'Overlay', icon: Broadcast, match: '/overlay' },
    { to: '/dashboard/settings', label: 'Pengaturan', icon: GearSix, match: '/settings' },
  ];

  return (
    <div className="min-h-screen bg-background pb-20">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6">
          {/* Navigation - Minimalist & Scrollable for Mobile */}
          <aside className="lg:col-span-3">
            <div className="sticky top-18 lg:top-24 z-30 bg-background/95 backdrop-blur lg:bg-white lg:border-2 lg:border-foreground lg:shadow-brutal lg:p-5">
              {/* Profile Info - Hidden on Mobile to save space */}
              <div className="hidden lg:flex items-center gap-3 mb-5 border-b-2 border-foreground pb-4 uppercase tracking-tighter">
                {user?.picture ? (
                  <img src={resolveFileUrl(user.picture)} alt="" className="w-10 h-10 border-2 border-foreground object-cover" />
                ) : (
                  <div className="w-10 h-10 border-2 border-foreground bg-primary flex items-center justify-center shadow-brutal-sm">
                    <UserCircle size={20} weight="bold" color="white" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{user?.name}</p>
                  <p className="text-[10px] font-bold text-muted-foreground truncate italic">@{user?.username}</p>
                </div>
              </div>

              {/* Menu Nav - Improved Horizontal Scroll */}
              <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto no-scrollbar py-2 px-4 shadow-inner lg:shadow-none lg:p-0 border-b-2 border-foreground/10 lg:border-none">
                {items.map((it) => {
                  const active = current === it.match;
                  return (
                    <Link
                      key={it.to}
                      to={it.to}
                      className={`inline-flex items-center gap-2 px-4 py-2 text-xs md:text-sm font-bold transition-all border-2 whitespace-nowrap shrink-0 ${
                        active
                          ? 'border-foreground bg-primary text-white shadow-brutal-sm scale-105 lg:scale-100'
                          : 'border-transparent text-foreground/60 hover:text-foreground hover:bg-white/50'
                      }`}
                    >
                      <it.icon size={16} weight="bold" />
                      <span>{it.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="hidden lg:block mt-6 pt-4 border-t-2 border-foreground">
                {user?.username && (
                  <Link
                    to={`/u/${user.username}`}
                    className="w-full inline-flex items-center justify-center gap-2 border-2 border-foreground bg-secondary px-3 py-2 text-[10px] font-bold uppercase tracking-widest shadow-brutal-sm hover:shadow-none transition-all"
                  >
                    <UserCircle size={14} weight="bold" /> Tampilkan Profil
                  </Link>
                )}
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="lg:col-span-9 min-w-0 px-1">
            <Routes>
              <Route index element={<DashOverview />} />
              <Route path="transactions" element={<DashTransactions />} />
              <Route path="units" element={<DashUnits />} />
              <Route path="goals" element={<DashGoals />} />
              <Route path="withdrawals" element={<DashWithdrawals />} />
              <Route path="overlay" element={<DashOverlay />} />
              <Route path="settings" element={<DashSettings />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
